import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate, useParams } from "react-router-dom";
import { PublicNav, Footer } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, Check, Scissors } from "lucide-react";
import { toast, Toaster } from "sonner";

const STEPS = ["Prestation", "Coiffeur", "Date", "Créneau", "Coordonnées"];

export default function Booking() {
  const [params] = useSearchParams();
  const { slug } = useParams();
  const base = slug ? `/salon/${slug}` : "";
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(null);
  const [serviceId, setServiceId] = useState(params.get("service") || "");
  const [staffId, setStaffId] = useState("");
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ first: "", last: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    api.get(slug ? `/salon/${slug}` : "/salon").then((r) => setData(r.data)).catch(() => {});
  }, [slug]);

  const service = useMemo(
    () => data?.services?.find((s) => s.id === serviceId),
    [data, serviceId]
  );
  const staffMember = useMemo(() => data?.staff?.find((m) => m.id === staffId), [data, staffId]);
  const staffLabel = staffId === "any" ? "Sans préférence" : (staffMember?.name || "");

  useEffect(() => {
    if (step !== 3 || !service || !date) return;
    let cancelled = false;
    const iso = date.toISOString().slice(0, 10);
    (async () => {
      await Promise.resolve();
      if (cancelled) return;
      setLoadingSlots(true);
      setSlot("");
      try {
        const r = await api.get("/availability", { params: { service_id: service.id, date: iso, staff_id: staffId || "any", slug: slug || undefined } });
        if (!cancelled) setSlots(r.data.slots);
      } catch {
        if (!cancelled) setSlots([]);
      } finally {
        if (!cancelled) setLoadingSlots(false);
      }
    })();
    return () => { cancelled = true; };
  }, [step, service, date, staffId]);

  const canNext = (step === 0 && !!serviceId) || (step === 1 && !!staffId) || (step === 2 && !!date) || (step === 3 && !!slot);

  const submit = async () => {
    if (!service || !date || !slot) return;
    setSubmitting(true);
    try {
      const { data: appt } = await api.post("/appointments", {
        service_id: service.id,
        date: date.toISOString().slice(0, 10),
        start_time: slot,
        client_first_name: form.first,
        client_last_name: form.last,
        client_email: form.email,
        client_phone: form.phone,
        staff_id: staffId,
        slug: slug || undefined,
      });
      setConfirmed(appt);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="min-h-screen bg-[#0A0908] text-[#F4F0EA]">
        <PublicNav />
        <Toaster theme="dark" />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="booking-confirmed">
          <div className="w-20 h-20 mx-auto mb-10 rounded-full border border-[#C5A059] flex items-center justify-center">
            <Check className="w-10 h-10 text-[#C5A059]" strokeWidth={1.5} />
          </div>
          <div className="text-[10px] tracking-[0.4em] text-[#C5A059] uppercase mb-4">Confirmé</div>
          <h1 className="font-serif text-5xl font-light mb-8">Votre rendez-vous est pris.</h1>
          <p className="text-[#A39E93] text-lg font-light mb-12">
            Un email de confirmation vous a été envoyé à <span className="text-[#F4F0EA]">{confirmed.client_email}</span>.
          </p>
          <div className="bg-[#141311] border border-[#2C2A26] p-8 text-left max-w-md mx-auto">
            <div className="flex justify-between py-3 border-b border-[#2C2A26]">
              <span className="text-[#A39E93] text-sm uppercase tracking-[0.15em]">Prestation</span>
              <span>{confirmed.service_name}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-[#2C2A26]">
              <span className="text-[#A39E93] text-sm uppercase tracking-[0.15em]">Coiffeur</span>
              <span>{confirmed.staff_name}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-[#2C2A26]">
              <span className="text-[#A39E93] text-sm uppercase tracking-[0.15em]">Date</span>
              <span>{confirmed.date}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-[#2C2A26]">
              <span className="text-[#A39E93] text-sm uppercase tracking-[0.15em]">Heure</span>
              <span>{confirmed.start_time} — {confirmed.end_time}</span>
            </div>
            <div className="flex justify-between py-3">
              <span className="text-[#A39E93] text-sm uppercase tracking-[0.15em]">Prix</span>
              <span className="text-[#C5A059]">{confirmed.price}€</span>
            </div>
          </div>
          <div className="mt-10 p-5 border border-[#C5A059]/30 bg-[#C5A059]/5" data-testid="manage-link-box">
            <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-2">Besoin de modifier ou annuler ?</div>
            <p className="text-sm text-[#A39E93] mb-3">Conservez ce lien personnel pour gérer votre rendez-vous à tout moment, sans créer de compte.</p>
            <button onClick={() => navigate(`/mon-rendez-vous/${confirmed.manage_token}`)} className="btn-secondary" data-testid="manage-booking-btn">Gérer mon rendez-vous</button>
          </div>
          <button onClick={() => navigate(base || "/")} className="btn-primary mt-8" data-testid="back-home-btn">Retour à l&apos;accueil</button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen bg-[#0A0908] flex items-center justify-center text-[#A39E93]">Chargement…</div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0908] text-[#F4F0EA]">
      <PublicNav />
      <Toaster theme="dark" />

      <div className="max-w-4xl mx-auto px-6 py-16 lg:py-24">
        {/* Stepper */}
        <div className="flex items-center justify-between mb-16" data-testid="booking-stepper">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center flex-1 last:flex-none">
              <div className={`flex items-center gap-3 ${i === step ? "text-[#C5A059]" : i < step ? "text-[#F4F0EA]" : "text-[#6B6760]"}`}>
                <div className={`w-8 h-8 rounded-full border flex items-center justify-center text-xs ${i === step ? "border-[#C5A059] bg-[#C5A059] text-[#0A0908] font-semibold" : i < step ? "border-[#C5A059]" : "border-[#2C2A26]"}`}>
                  {i < step ? <Check className="w-4 h-4" strokeWidth={2} /> : i + 1}
                </div>
                <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em]">{label}</span>
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-[#2C2A26] mx-3 sm:mx-6" />}
            </div>
          ))}
        </div>

        {/* Step content */}
        {step === 0 && (
          <div data-testid="step-service">
            <h2 className="font-serif text-4xl sm:text-5xl font-light mb-12">Choisissez votre prestation.</h2>
            <div className="space-y-3">
              {data.services.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setServiceId(s.id)}
                  className={`w-full text-left p-6 border transition flex justify-between items-center ${serviceId === s.id ? "border-[#C5A059] bg-[#C5A059]/5" : "border-[#2C2A26] hover:border-[#A39E93]"}`}
                  data-testid={`service-option-${s.name.replace(/\s+/g,'-').toLowerCase()}`}
                >
                  <div>
                    <div className="font-serif text-2xl font-light">{s.name}</div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#6B6760] mt-1">{s.duration} minutes</div>
                  </div>
                  <div className="text-2xl text-[#C5A059] font-light">{s.price}€</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="step-staff">
            <h2 className="font-serif text-4xl sm:text-5xl font-light mb-12">Choisissez votre coiffeur.</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => setStaffId("any")}
                className={`text-left p-6 border transition flex items-center gap-4 ${staffId === "any" ? "border-[#C5A059] bg-[#C5A059]/5" : "border-[#2C2A26] hover:border-[#A39E93]"}`}
                data-testid="staff-option-any"
              >
                <div className="w-14 h-14 rounded-full border border-[#2C2A26] flex items-center justify-center text-[#C5A059] shrink-0">
                  <Scissors className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-serif text-2xl font-light">Sans préférence</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#6B6760] mt-1">Premier coiffeur disponible</div>
                </div>
              </button>
              {(data.staff || []).map((m) => (
                <button
                  key={m.id}
                  onClick={() => setStaffId(m.id)}
                  className={`text-left p-6 border transition flex items-center gap-4 ${staffId === m.id ? "border-[#C5A059] bg-[#C5A059]/5" : "border-[#2C2A26] hover:border-[#A39E93]"}`}
                  data-testid={`staff-option-${m.id}`}
                >
                  <div className="w-14 h-14 rounded-full overflow-hidden border shrink-0 bg-[#0A0908] flex items-center justify-center" style={{ borderColor: m.color }}>
                    {m.photo_url
                      ? <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover" />
                      : <span className="font-serif text-xl" style={{ color: m.color }}>{m.name.charAt(0)}</span>}
                  </div>
                  <div>
                    <div className="font-serif text-2xl font-light">{m.name}</div>
                    {m.title && <div className="text-xs uppercase tracking-[0.2em] text-[#6B6760] mt-1">{m.title}</div>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-date">
            <h2 className="font-serif text-4xl sm:text-5xl font-light mb-12">Choisissez une date.</h2>
            <div className="flex justify-center">
              <div className="bg-[#141311] border border-[#2C2A26] p-6">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                  weekStartsOn={1}
                />
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="step-slot">
            <h2 className="font-serif text-4xl sm:text-5xl font-light mb-4">Choisissez un créneau.</h2>
            <p className="text-[#A39E93] mb-12 font-light">
              {service?.name} · {service?.duration} min · {staffLabel} — {date?.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
            </p>
            {loadingSlots ? (
              <div className="text-[#A39E93] text-sm uppercase tracking-[0.3em]">Chargement…</div>
            ) : slots.length === 0 ? (
              <div className="text-[#A39E93] py-8 text-center border border-[#2C2A26]">
                Aucun créneau disponible pour cette date. Veuillez choisir un autre jour.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="slots-grid">
                {slots.map((t) => (
                  <button
                    key={t}
                    onClick={() => setSlot(t)}
                    className={`py-3 border text-sm transition ${slot === t ? "border-[#C5A059] bg-[#C5A059] text-[#0A0908] font-semibold" : "border-[#2C2A26] hover:border-[#C5A059] text-[#F4F0EA]"}`}
                    data-testid={`slot-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div data-testid="step-details">
            <h2 className="font-serif text-4xl sm:text-5xl font-light mb-12">Vos coordonnées.</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-xl">
              <div>
                <Label htmlFor="first" className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Prénom</Label>
                <Input id="first" value={form.first} onChange={(e) => setForm({...form, first: e.target.value})} className="mt-2 bg-[#141311] border-[#2C2A26] focus:ring-[#C5A059]" data-testid="input-first" />
              </div>
              <div>
                <Label htmlFor="last" className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Nom</Label>
                <Input id="last" value={form.last} onChange={(e) => setForm({...form, last: e.target.value})} className="mt-2 bg-[#141311] border-[#2C2A26]" data-testid="input-last" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Email</Label>
                <Input id="email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-2 bg-[#141311] border-[#2C2A26]" data-testid="input-email" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="phone" className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Téléphone</Label>
                <Input id="phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="mt-2 bg-[#141311] border-[#2C2A26]" data-testid="input-phone" />
              </div>
            </div>

            <div className="mt-12 p-6 bg-[#141311] border border-[#2C2A26] max-w-xl">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-3">Récapitulatif</div>
              <div className="text-[#F4F0EA] font-serif text-2xl font-light">{service?.name}</div>
              <div className="text-[#A39E93] text-sm mt-2">{date?.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} à {slot}</div>
              <div className="text-[#A39E93] text-sm mt-1">Avec {staffLabel}</div>
              <div className="text-[#C5A059] text-xl mt-3">{service?.price}€ · {service?.duration} min</div>
            </div>
          </div>
        )}

        {/* Footer nav */}
        <div className="mt-16 flex justify-between items-center">
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)} className="text-[#A39E93] hover:text-[#F4F0EA] text-xs uppercase tracking-[0.25em] inline-flex items-center gap-2" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Retour
            </button>
          ) : <Link to={base || "/"} className="text-[#A39E93] hover:text-[#F4F0EA] text-xs uppercase tracking-[0.25em]" data-testid="back-home">Accueil</Link>}

          {step < 4 ? (
            <button
              disabled={!canNext}
              onClick={() => setStep(step + 1)}
              className="btn-primary disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="next-btn"
            >
              Continuer <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          ) : (
            <button
              disabled={submitting || !form.first || !form.last || !form.email || !form.phone}
              onClick={submit}
              className="btn-primary disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="confirm-booking-btn"
            >
              {submitting ? "Envoi…" : (<><Scissors className="w-4 h-4" strokeWidth={1.5} /> Confirmer</>)}
            </button>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
